import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Particle from "../Particle";
import { AiOutlineMail } from "react-icons/ai";
import { FaLinkedinIn } from "react-icons/fa";
import {
  contactEmail,
  contactGmailComposeUrl,
  linkedinUrl,
} from "../../data/siteMeta";
import usePageMeta from "../../hooks/usePageMeta";
import { getSkillPresentation } from "../shared/skillPresentation";

function ResumeNew() {
  usePageMeta(
    "Experience | Om Jaiswal",
    "Experience, strengths, and delivery habits for Om Jaiswal across full stack websites, web apps, APIs, and product integrations."
  );

  const experience = [
    {
      domain: "Healthcare product work",
      role: "Full Stack Developer",
      period: "Current role",
      bullets: [
        "Build healthcare product flows across UI, API, and backend modules.",
        "Ship features with close attention to usability and data reliability.",
      ],
    },
    {
      domain: "Fintech product work",
      role: "Full Stack Developer",
      period: "Feb 2024 - Jul 2024",
      bullets: [
        "Delivered verification and document-processing flows with practical frontend behavior.",
        "Turned extraction and validation steps into screens people could use confidently.",
      ],
    },
    {
      domain: "Analytics reporting internship",
      role: "Data Analyst Intern",
      period: "Jun 2023 - Jul 2023",
      bullets: [
        "Built dashboards and reports that surfaced useful sales patterns fast.",
      ],
    },
  ];

  const strengths = [
    "React.js",
    "Next.js",
    "TypeScript",
    "JavaScript",
    "Node.js",
    "Express.js",
    "PostgreSQL",
    "MongoDB",
    "REST APIs",
    "Tailwind CSS",
    "Bootstrap",
    "Responsive UI",
    "Django",
    "Component systems",
    "Web app UX",
    "Frontend architecture",
    "CI/CD pipelines",
    "AWS basics",
  ];
  const strengthItems = strengths.map((strength) => ({
    strength,
    ...getSkillPresentation(strength),
  }));

  const currentFocus = [
    "Full stack websites and web apps",
    "Portals and dashboards with clean data flows",
    "REST APIs inside product experiences",
    "Integrations that reduce friction",
  ];

  const clientSignals = [
    "Translate fuzzy requirements into clear plans",
    "Stay close to UX so products feel intentional",
    "Prefer maintainable systems over clever fragility",
    "Keep stakeholders informed with low-noise updates",
  ];

  return (
    <Container fluid className="resume-section">
      <Particle />
      <Container className="resume-shell section-shell">
        <div className="resume-hero">
          <div className="resume-hero-text">
            <p className="section-kicker">Experience</p>
            <h1 className="section-title page-hero-title">
              Full stack experience across websites, web apps, and APIs.
            </h1>
            <p className="section-copy">
              React interfaces, Node APIs, and data layers shaped into products
              that feel dependable to use.
            </p>

            <div className="resume-hero-actions">
              <Button as="a" href={contactGmailComposeUrl}>
                <AiOutlineMail />
                Email Me
              </Button>
              <Button
                as="a"
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline-light"
                aria-label="Open Om Jaiswal LinkedIn profile in a new tab"
              >
                <FaLinkedinIn />
                LinkedIn
              </Button>
            </div>

            <p className="resume-contact-line">
              Pune, India · {contactEmail}
            </p>
          </div>

          <div className="resume-hero-visual" aria-hidden="true">
            <svg
              className="resume-stack-svg"
              viewBox="0 0 300 332"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Connector rail */}
              <line x1="24" y1="48" x2="24" y2="252" stroke="#60d5ff" strokeWidth="1.5" strokeDasharray="3 6" opacity="0.3"/>

              {/* ── Card 1: Frontend Layer ── */}
              <rect x="0" y="8" width="290" height="92" rx="14" fill="#131c2e" stroke="#1e2d46" strokeWidth="1.5"/>
              <circle cx="24" cy="54" r="13" fill="#60d5ff" fillOpacity="0.12" stroke="#60d5ff" strokeWidth="1.5"/>
              {/* </> icon */}
              <path d="M19.5 51 L16 54 L19.5 57" stroke="#60d5ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M28.5 51 L32 54 L28.5 57" stroke="#60d5ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="23" y1="49" x2="25" y2="59" stroke="#60d5ff" strokeWidth="1.5" strokeLinecap="round"/>
              <text x="46" y="46" fill="#e2e8f0" fontSize="11.5" fontWeight="700" fontFamily="system-ui,sans-serif">Frontend Layer</text>
              <text x="46" y="62" fill="#8892a4" fontSize="9.5" fontFamily="system-ui,sans-serif">React · Next.js · TypeScript · Tailwind</text>
              {/* pills */}
              <rect x="46" y="72" width="52" height="17" rx="8.5" fill="#60d5ff" fillOpacity="0.08" stroke="#60d5ff" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="72" y="83.5" fill="#60d5ff" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">UI / UX</text>
              <rect x="104" y="72" width="64" height="17" rx="8.5" fill="#60d5ff" fillOpacity="0.08" stroke="#60d5ff" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="136" y="83.5" fill="#60d5ff" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">Responsive</text>
              <rect x="174" y="72" width="68" height="17" rx="8.5" fill="#60d5ff" fillOpacity="0.08" stroke="#60d5ff" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="208" y="83.5" fill="#60d5ff" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">Component sys</text>

              {/* ── Card 2: API Layer ── */}
              <rect x="0" y="110" width="290" height="92" rx="14" fill="#131c2e" stroke="#1e2d46" strokeWidth="1.5"/>
              <circle cx="24" cy="156" r="13" fill="#a78bfa" fillOpacity="0.12" stroke="#a78bfa" strokeWidth="1.5"/>
              {/* Server icon */}
              <rect x="16.5" y="150" width="15" height="5" rx="1.5" stroke="#a78bfa" strokeWidth="1.2" fill="none"/>
              <rect x="16.5" y="157" width="15" height="5" rx="1.5" stroke="#a78bfa" strokeWidth="1.2" fill="none"/>
              <circle cx="28.5" cy="152.5" r="1.2" fill="#a78bfa"/>
              <circle cx="28.5" cy="159.5" r="1.2" fill="#a78bfa"/>
              <text x="46" y="148" fill="#e2e8f0" fontSize="11.5" fontWeight="700" fontFamily="system-ui,sans-serif">API Layer</text>
              <text x="46" y="164" fill="#8892a4" fontSize="9.5" fontFamily="system-ui,sans-serif">Node.js · Express.js · REST APIs</text>
              {/* pills */}
              <rect x="46" y="174" width="52" height="17" rx="8.5" fill="#a78bfa" fillOpacity="0.08" stroke="#a78bfa" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="72" y="185.5" fill="#a78bfa" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">Auth</text>
              <rect x="104" y="174" width="72" height="17" rx="8.5" fill="#a78bfa" fillOpacity="0.08" stroke="#a78bfa" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="140" y="185.5" fill="#a78bfa" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">Integrations</text>
              <rect x="182" y="174" width="52" height="17" rx="8.5" fill="#a78bfa" fillOpacity="0.08" stroke="#a78bfa" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="208" y="185.5" fill="#a78bfa" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">CRUD</text>

              {/* ── Card 3: Data Layer ── */}
              <rect x="0" y="212" width="290" height="79" rx="14" fill="#131c2e" stroke="#1e2d46" strokeWidth="1.5"/>
              <circle cx="24" cy="251.5" r="13" fill="#34d399" fillOpacity="0.12" stroke="#34d399" strokeWidth="1.5"/>
              {/* DB cylinder icon */}
              <ellipse cx="24" cy="245.5" rx="6" ry="2.2" stroke="#34d399" strokeWidth="1.2" fill="none"/>
              <line x1="18" y1="245.5" x2="18" y2="255" stroke="#34d399" strokeWidth="1.2"/>
              <line x1="30" y1="245.5" x2="30" y2="255" stroke="#34d399" strokeWidth="1.2"/>
              <ellipse cx="24" cy="255" rx="6" ry="2.2" stroke="#34d399" strokeWidth="1.2" fill="none"/>
              <text x="46" y="243.5" fill="#e2e8f0" fontSize="11.5" fontWeight="700" fontFamily="system-ui,sans-serif">Data Layer</text>
              <text x="46" y="259.5" fill="#8892a4" fontSize="9.5" fontFamily="system-ui,sans-serif">PostgreSQL · MongoDB</text>
              {/* pills */}
              <rect x="46" y="269" width="80" height="17" rx="8.5" fill="#34d399" fillOpacity="0.08" stroke="#34d399" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="86" y="280.5" fill="#34d399" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">Schema design</text>
              <rect x="132" y="269" width="65" height="17" rx="8.5" fill="#34d399" fillOpacity="0.08" stroke="#34d399" strokeOpacity="0.25" strokeWidth="1"/>
              <text x="164" y="280.5" fill="#34d399" fontSize="8.5" textAnchor="middle" fontFamily="system-ui,sans-serif">Data flows</text>

              {/* ── Floating badge ── */}
              <rect x="186" y="298" width="104" height="26" rx="13" fill="#0d1526" stroke="#60d5ff" strokeOpacity="0.4" strokeWidth="1"/>
              <circle cx="203" cy="311" r="4" fill="#60d5ff" fillOpacity="0.3"/>
              <circle cx="203" cy="311" r="2" fill="#60d5ff"/>
              <text x="213" y="315" fill="#60d5ff" fontSize="8.5" fontFamily="system-ui,sans-serif">End-to-end delivery</text>
            </svg>
          </div>
        </div>

        <Row className="resume-grid g-4">
          <Col lg={7}>
            <div className="resume-side-col">
              <section className="resume-card">
                <h2>Experience</h2>
                {experience.map((item) => (
                  <article key={`${item.domain}-${item.role}`} className="experience-item">
                    <div className="experience-meta">
                      <div>
                        <h3>{item.domain}</h3>
                        <p>{item.role}</p>
                      </div>
                      <span className="experience-period">{item.period}</span>
                    </div>
                    <ul className="experience-bullets">
                      {item.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </section>

              <section className="resume-card">
                <h2>What I usually help build</h2>
                <ul className="resume-side-list">
                  <li>Full stack websites and web apps</li>
                  <li>Business sites and landing pages</li>
                  <li>REST APIs behind product interfaces</li>
                  <li>Dashboards, portals, and admin tools</li>
                  <li>Supporting integrations that remove friction</li>
                </ul>
              </section>
            </div>
          </Col>

          <Col lg={5}>
            <div className="resume-side-col">
              <section className="resume-card">
                <h2>Core strengths</h2>
                <div className="resume-pill-grid">
                  {strengthItems.map(({ strength, Icon, color }) => (
                    <span
                      key={strength}
                      className="resume-pill"
                      style={{ "--resume-pill-color": color }}
                    >
                      <span className="resume-pill-icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <span className="resume-pill-label">{strength}</span>
                    </span>
                  ))}
                </div>
              </section>

              <section className="resume-card">
                <h2>Current delivery focus</h2>
                <ul className="resume-side-list">
                  {currentFocus.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="resume-card">
                <h2>Client-facing habits</h2>
                <ul className="resume-side-list">
                  {clientSignals.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default ResumeNew;
